/**
 * QuantumFlowSync.AI - Permission Gate Components
 * Conditional rendering based on permissions, tiers, and roles
 */
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermission, useTier, checkPermission } from '../hooks/usePermission';

/**
 * Permission Gate Component
 * Conditionally renders children based on permission
 */
export const PermissionGate = ({
  resource,
  scope,
  action,
  children,
  fallback = null
}) => {
  const hasPermission = usePermission(resource, scope, action);

  if (!hasPermission) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Tier Gate Component
 * Conditionally renders children based on tier
 */
export const TierGate = ({
  tier,
  children,
  fallback = null
}) => {
  const hasTier = useTier(tier);

  if (!hasTier) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Role Gate Component
 * Conditionally renders children based on role
 */
export const RoleGate = ({
  role,
  children,
  fallback = null
}) => {
  const { employee } = useAuth();

  const hasRole = employee?.roles?.some(r => r.name === role);

  if (!hasRole) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Any Permission Gate
 * Renders if employee has ANY of the specified permissions
 */
export const AnyPermissionGate = ({
  permissions,
  children,
  fallback = null
}) => {
  const { employee } = useAuth();

  const hasAny = permissions.some(({ resource, scope, action }) => {
    return checkPermission(employee, resource, scope, action);
  });

  if (!hasAny) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * All Permissions Gate
 * Renders if employee has ALL of the specified permissions
 */
export const AllPermissionsGate = ({
  permissions,
  children,
  fallback = null
}) => {
  const { employee } = useAuth();

  const hasAll = permissions.every(({ resource, scope, action }) => {
    return checkPermission(employee, resource, scope, action);
  });

  if (!hasAll) {
    return fallback;
  }

  return <>{children}</>;
};

export default PermissionGate;
